import {
  addPriceRange,
  addSelectedFilter,
  cacheProductsRequestData,
  clearAvailability,
  clearFacets,
  clearProducts,
  clearSelectedDepartment,
  clearSelectedFacet,
  clearSelectedFilters,
  clearSelectedFiltersMinusCatalogs,
  removeSelectedFilter,
  requestProducts,
  requestProductsById,
  resetCursor,
  selectFacet,
  selectProduct,
  selectShopDepartment,
  setAdminCartId,
  setIsShopping,
  setSearchTerm,
  storeActiveSubTypes,
  storeAllSubTypes,
  toggleIsAdminCart,
  togglePriceRangeSet,
  requestMinRules,
  setAllSizesAvailable,
  setIncludeUnavailableProduct,
  requestProductDetailsV2
} from './actions';

import { requestCurrentCartProductsDetails } from '../carts/actions';

import {
  APPAREL_SUBTYPES,
  EQUIPMENT_SUBTYPES,
  FOOTWEAR_SUBTYPES,
  GRIDWALL_MAX_PRICE,
  GRIDWALL_MIN_PRICE
} from './constants';

import {
  reconcileDescription,
  mergeFacets,
  groupProductDetailSizeAvailabilities,
  sortByAvailability
} from './util/helpers';

export const defaultState = {
  isFetching: false,
  products: {
    dept: null,
    count: 0,
    styles: []
  },
  productRequestData: null,
  selectedProduct: null,
  after: null,
  productAvailability: {},
  productFutureAvailability: {},
  productMinRules: null,
  productMaxRules: null,
  selectedDepartment: null,
  facets: {
    facets: [],
    selected: {}
  },
  activeSubTypes: [],
  allSubTypes: [],
  selectedFilters: {},
  priceRangeSet: false,
  priceRange: {
    wholesaleMin: GRIDWALL_MIN_PRICE,
    wholesaleMax: GRIDWALL_MAX_PRICE
  },
  searchTerm: {},
  isAdminCart: false,
  adminCartId: null,
  isShopping: false,
  allSizesAvailable: false,
  includeUnavailableProduct: false,
  selectedStyleDetails: {},
  isFetchingStyleDetails: false
};

const shop = (state = defaultState, action) => {
  switch (action.type) {
    case requestProducts.event.success: {
      const { count, cursor, dept, facets, styles, updateFilters, hasUnavailableProducts } = action.payload;

      const sortedStyles = hasUnavailableProducts ? sortByAvailability(styles) : styles;

      return {
        ...state,
        isFetching: false,
        products: {
          dept,
          count,
          styles: reconcileDescription([...state.products.styles, ...sortedStyles])
        },
        // Avoid position reset when an empty product list is accompanied by a null cursor
        after: cursor && cursor.after,
        facets: {
          ...state.facets,
          // If no products in catalog, facets to be passed as an empty array
          facets: mergeFacets(styles.length ? facets : [], state.facets.facets, state.selectedFilters, updateFilters)
        }
      };
    }
    case requestCurrentCartProductsDetails.event.error:
    case requestMinRules.event.error:
    case requestProducts.event.error:
    case requestProductsById.event.error:
    case requestProductsById.event.success:
      return { ...state, isFetching: false };
    case cacheProductsRequestData.type:
      return { ...state, productRequestData: action.payload };
    case requestCurrentCartProductsDetails.event.start:
    case requestMinRules.event.start:
    case requestProducts.event.start:
    case requestProductsById.event.start:
      return { ...state, isFetching: true };
    case clearProducts.type:
      return { ...state, products: defaultState.products };
    case selectProduct.type:
      return { ...state, selectedProduct: action.payload };
    case resetCursor.type:
      return { ...state, after: null };
    case setSearchTerm.type:
      return { ...state, searchTerm: action.payload };
    case requestMinRules.event.success:
      return { ...state, isFetching: false, productMinRules: action.payload };
    case selectShopDepartment.type:
      return {
        ...state,
        selectedDepartment: action.payload,
        selectedFilters: defaultState.selectedFilters,
        priceRange: defaultState.priceRange,
        facets: {
          ...state.facets,
          selected: defaultState.facets.selected
        }
      };
    case clearSelectedFacet.type:
      return {
        ...state,
        facets: {
          ...state.facets,
          selected: Object.keys(state.facets.selected).reduce((acc, key) => {
            if (key !== action.payload) {
              acc[key] = state.facets.selected[key];
            }

            return acc;
          }, {})
        }
      };
    case storeAllSubTypes.type:
      return { ...state, allSubTypes: state.facets.facets[7].values };
    case storeActiveSubTypes.type:
      if (action.payload.value === 'APRL') {
        return { ...state, activeSubTypes: state.allSubTypes.filter(subType => APPAREL_SUBTYPES.includes(subType)) };
      }

      if (action.payload.value === 'FTWR') {
        return { ...state, activeSubTypes: state.allSubTypes.filter(subType => FOOTWEAR_SUBTYPES.includes(subType)) };
      }

      return { ...state, activeSubTypes: state.allSubTypes.filter(subType => EQUIPMENT_SUBTYPES.includes(subType)) };
    case selectFacet.type: {
      if (action.payload.value) {
        return {
          ...state,
          facets: {
            ...state.facets,
            selected: { ...state.facets.selected, [action.payload.key]: action.payload.value }
          }
        };
      }

      return { ...state, facets: { ...state.facets, selected: defaultState.facets.selected } };
    }
    case addSelectedFilter.type:
      if (action.payload.key in state.selectedFilters) {
        return {
          ...state,
          selectedFilters: {
            ...state.selectedFilters,
            // remove duplicates
            [action.payload.key]: Array.from(
              new Set([...state.selectedFilters[action.payload.key], action.payload.value])
            )
          }
        };
      }

      return {
        ...state,
        selectedFilters: { ...state.selectedFilters, [action.payload.key]: [action.payload.value] }
      };
    case removeSelectedFilter.type: {
      const removedFilter = action.payload;
      const updatedFilters = Object.entries(state.selectedFilters).reduce((acc, [key, values]) => {
        if (key === removedFilter.key) {
          acc[key] = values.filter(value => value !== removedFilter.value); // filter out the removed value

          if (acc[key].length === 0) {
            delete acc[key];
          }
        } else {
          acc[key] = values;
        }
        return acc;
      }, {});

      return {
        ...state,
        selectedFilters: updatedFilters
      };
    }
    case addPriceRange.type:
      return { ...state, priceRange: { wholesaleMin: action.payload[0], wholesaleMax: action.payload[1] } };
    case clearFacets.type:
      return {
        ...state,
        facets: {
          ...state.facets,
          selected: defaultState.facets.selected
        }
      };
    case clearSelectedFilters.type:
      return {
        ...state,
        selectedFilters: defaultState.selectedFilters,
        priceRange: defaultState.priceRange,
        priceRangeSet: false
      };
    case clearSelectedFiltersMinusCatalogs.type:
      return {
        ...state,
        selectedFilters: { catalogs: [...state.selectedFilters.catalogs] },
        priceRange: defaultState.priceRange,
        priceRangeSet: false
      };
    case clearAvailability.type:
      return {
        ...state,
        productAvailability: defaultState.productAvailability,
        productFutureAvailability: defaultState.productFutureAvailability
      };
    case toggleIsAdminCart.type:
      return { ...state, isAdminCart: !state.isAdminCart };
    case togglePriceRangeSet.type:
      return { ...state, priceRangeSet: action.payload };
    case setAdminCartId.type:
      return { ...state, adminCartId: action.payload };
    case setIsShopping.type:
      return { ...state, isShopping: action.payload };
    case clearSelectedDepartment.type:
      return { ...state, selectedDepartment: null };
    case setAllSizesAvailable.type:
      return { ...state, allSizesAvailable: action.payload };
    case setIncludeUnavailableProduct.type:
      return { ...state, includeUnavailableProduct: action.payload };
    case requestProductDetailsV2.event.start:
      return { ...state, isFetchingStyleDetails: true, isFetching: true };
    case requestProductDetailsV2.event.error:
      return { ...state, isFetchingStyleDetails: false, isFetching: false };
    case requestProductDetailsV2.event.success:
    case requestCurrentCartProductsDetails.event.success: {
      const { availabilities, futureAvailabilities } = groupProductDetailSizeAvailabilities(action.payload);
      return {
        ...state,
        isFetchingStyleDetails: false,
        isFetching: false,
        selectedStyleDetails: action.payload.reduce(
          (detailsByStyleNumber, style) => ({
            ...detailsByStyleNumber,
            [style.productCode]: {
              ...style.copy,
              maxAmountOrderable: style.maxAmountOrderable
            }
          }),
          state.selectedStyleDetails
        ),
        productAvailability: {
          ...state.productAvailability,
          ...availabilities
        },
        productFutureAvailability: {
          ...state.productFutureAvailability,
          ...futureAvailabilities
        }
      };
    }
    default:
      return state;
  }
};

export default shop;
